import DefaultModal from '@/components/Modals/DefaultModal'
import ConfirmCancelModalFooter from '@/components/Modals/ConfirmCancelModalFooter'
import { Flex } from '@/components/Flex'
import { Headline2, InfoLabel, Label } from '@/components/Typography'
import { useModal } from '@/hooks/useModal'
import { DefaultModalProp } from '@/typings/Modals'

import { useApplyClusterConfig } from '../application/hooks/useApplyClusterConfig'
import { useUpdateCluster } from '../application/hooks/useUpdateCluster'

/**
 * Confirms a VLAN change on a live cluster, then delivers it: the write (PATCH)
 * lands the new tag, and the reapply ships it to the cluster. On a backend
 * refusal the dialog stays open — the error is surfaced by the update hook.
 *
 * Modals render outside the router tree (`<Modals />` is a sibling of `<App />`
 * in index.tsx), so no router hook may be used here — leaving the page is the
 * opener's job, through the `onSuccess` callback.
 */
export default function ConfirmClusterConfigChangeModal({
  setOpen,
  ...props
}: DefaultModalProp) {
  const modal = useModal('confirmClusterConfigChange')
  const { clusterId, clusterName, body } = modal.data

  const updateCluster = useUpdateCluster()
  const applyMutation = useApplyClusterConfig()

  const onConfirm = async () => {
    try {
      await updateCluster.mutateAsync({ id: clusterId, body })
    } catch {
      // The refusal was already surfaced by the update hook; keep the dialog
      // open so the change is not silently half-applied.
      return
    }

    applyMutation.mutate(clusterId, {
      onSuccess: () => {
        modal.close()
        modal.callbacks?.onSuccess()
      },
    })
  }

  return (
    <DefaultModal
      {...props}
      setOpen={setOpen}
      type="confirmClusterConfigChange"
      modalHeader={
        <Headline2>Apply network change to {clusterName}?</Headline2>
      }
      modalBody={
        <Flex flexDirection="column" width="100%" mt="1rem" gap="0.75rem">
          <Label>
            Saving re-delivers the cluster config: {clusterName} goes back into
            Setup in Progress and returns to Ready in a minute or two.
          </Label>
          <Label color="orange">
            Rebuilding the network objects briefly cuts everything riding the
            provider network — public VLAN networks, and DRBD storage
            replication where it shares the port — for a few seconds.
          </Label>
          <InfoLabel>
            Pick the moment: the blink hits running workloads, not just the
            control plane.
          </InfoLabel>
        </Flex>
      }
      modalFooter={ConfirmCancelModalFooter}
      isLoading={updateCluster.isPending || applyMutation.isPending}
      onConfirm={onConfirm}
      confirmButtonText="Save and re-apply"
      cancelButtonText="Cancel"
    />
  )
}
